import type {$Fetch} from 'ofetch';
import type {FetchOptions} from 'ofetch';
import type {AsyncDataOptions} from "#app";


class FetchFactory {
  private readonly $fetch: $Fetch;
  private readonly $fetchNoAuth: $Fetch;
  private $token: Ref<string|null>;

  constructor(fetcher: $Fetch, fetcherNoAuth: $Fetch, token: Ref<string|null>) {
    this.$fetch = fetcher;
    this.$fetchNoAuth = fetcherNoAuth;
    this.$token = token;
  }

  async call<T>(method: string, url: string, data?: object, fetchOptions: FetchOptions<'json'> = {}, withAuth: boolean = true): Promise<T> {
    if(withAuth) {
      const headers = Object.assign(
        { "Content-Type": 'application/json; charset=UTF-8' },
        fetchOptions.headers || {},
        this.$token.value ? { Authorization: this.$token.value } : {}
      );
      return this.$fetch<T>(url, {method, body: data, ...fetchOptions, headers});
    } else {
      return this.$fetchNoAuth<T>(url, {method, body: data, ...fetchOptions});
    }
  }

  async get<T>(url: string, key: string, asyncDataOptions?: AsyncDataOptions<T>) {
    const {data, error} = await useAsyncData(key, () => {
      return this.call<T>('GET', url);
    }, asyncDataOptions);
    if(error.value) {
      throw error.value;
    }
    return data.value;
  }

  async post<T>(url: string, key: string, payload: object, asyncDataOptions?: AsyncDataOptions<T>) {
    const {data, error} = await useAsyncData(key, () => {
      return this.call<T>('POST', url, payload);
    }, asyncDataOptions);
    if(error.value) {
      throw error.value;
    }
    return data.value;
  }

  async clientPost<T>(url: string, payload?: object, fetchOptions?: FetchOptions<'json'>) {
    return await this.call<T>('POST', url, payload, fetchOptions);
  }

  async clientGet<T>(url: string, fetchOptions?: FetchOptions<'json'>): Promise<T> {
    return await this.call<T>('GET', url, undefined, fetchOptions);
  }

  async clientGetNoAuth<T>(url: string, fetchOptions?: FetchOptions<'json'>): Promise<T> {
    return await this.call<T>('GET', url, undefined, fetchOptions, false);
  }

  async clientPostNoAuth<T>(url: string, payload?: object, fetchOptions?: FetchOptions<'json'>) {
    return await this.call<T>('POST', url, payload, fetchOptions, false);
  }

  async clientPut<T>(url: string, payload?: object, fetchOptions?: FetchOptions<'json'>) {
    return await this.call<T>('PUT', url, payload, fetchOptions);
  }
}

export default FetchFactory;
